export interface AddressContactInfo {
  name: string;
  email?: string;
  phone?: string;
}

export interface Address {
  addressId: string;
  street1: string;
  street2: string;
  city: string;
  state: string;
  zipCode: string;
  zipExtraFour: string;
  country: string;
  name: string;
  attnTo: string;
  contactInfo: AddressContactInfo;
  tddd: string | null;
}

export interface ContractInfo {
  contractId: string | null;
  contractDisplayName: string | null;
}

export interface PriceDetail {
  value: number;
  currency: string;
  type: string;
  displayValue: string;
}

export interface OrderPriceTotals {
  quantity: number;
  totalListPrice: PriceDetail | null;
  subtotalListPrice: PriceDetail;
  totalContractPrice: PriceDetail;
  subtotalContractPrice: PriceDetail;
  discountTotal: PriceDetail;
  totalTax: PriceDetail;
  taxesAndFees: PriceDetail[];
  promptPayEligible: boolean;
  promptPayTerms: string | null;
  subtotalContractPriceWithPromptPay: PriceDetail | null;
  totalContractPriceWithPromptPay: PriceDetail | null;
  totalTaxWithPromptPay: PriceDetail | null;
  isTaxComputed: boolean;
  taxOverrideDetails: string | null;
  taxExempt: boolean;
}

export interface HealthcareLicense {
  name: string;
  state: string;
  licenseNumber: string;
  expirationDate: string;
  licenseType: string;
}

export enum OrderStatus {
  OnHold = "ON_HOLD",
  InternalHold = "INTERNAL_HOLD",
  Created = "CREATED",
  Pending = "PENDING",
  Confirmed = "CONFIRMED",
  Cancelled = "CANCELLED",
  Completed = "COMPLETED",
  Fulfilled = "FULFILLED"
}

// MappedOrderStatus is used to map OrderStatus to a more user-friendly status
export enum MappedOrderStatus {
  OnHold = "ON_HOLD",
  Processing = "PROCESSING",
  Cancelled = "CANCELLED",
  Shipped = "SHIPPED"
}

export interface Order {
  orderId: string;
  orderStatus: OrderStatus;
  mappedOrderStatus: MappedOrderStatus;
  orderHoldReasons: string[];
  orderNumber: string;
  userId: string;
  oktaId: string;
  purchaseOrderNumber: string;
  contractInfo: ContractInfo;
  fein: string | null;
  totalQuantity: number;
  unitType: string | null;
  orderPriceTotals: OrderPriceTotals;
  orderReleasedToShipperDate: string;
  orderShippedDate: string | null;
  orderCreatedDate: string;
  orderCancelledDate: string | null;
  billToOrganizationId: string;
  billToAddress: Address;
  soldToOrganizationId: string;
  soldToAddress: Address;
  shipToOrganizationId: string;
  shipToAddress: Address;
  healthcareLicenses: HealthcareLicense[];
  reservationId: string | null;
  reservationNumber: string | null;
  paymentType: string;
  paymentMethodId: string | null;
  paymentId: string | null;
  lastModifiedDate: string;
  updatedBy: string | null;
  orderCancelReason: string | null;
  source: string;
  placedBy: string;
}

export interface OrderList {
  orders: {
    order: Order;
    placedByUser: {
      userId: string;
      oktaId: string;
      firstName: string;
      lastName: string;
      email: string;
      personId: string;
    };
    placedForUser: {
      userId: string;
      oktaId: string;
      firstName: string;
      lastName: string;
      email: string;
      personId: string;
    };
  }[];
  pagination: {
    nextPageKey: string | null;
    pageSize: number;
  };
  totalPages: number;
}

export enum OrderSortableColumnKeys {
  DatePlaced = "OrderCreateDate",
  Status = "OrderStatus",
  MappedStatus = "MappedOrderStatus"
}
